import React from 'react';
import { useAuth } from '../context/AuthContext';
import type { SavedLocation } from '../context/AuthContext';

interface LocationSwitcherProps {
  /** Shown when the active location has no custom name (e.g. "Hagerstown, MD"). */
  fallbackLabel: string;
}

/**
 * Displays the active location's name. With two or more saved locations the name
 * becomes a native select (styled as inline text) so phones get their own picker.
 */
function LocationSwitcher({ fallbackLabel }: LocationSwitcherProps) {
  const { savedLocations, activeLocation, setActiveZipcode } = useAuth();
  return <LocationSelector fallbackLabel={fallbackLabel} savedLocations={savedLocations} activeLocation={activeLocation} onChange={setActiveZipcode} />;
}

export function LocationSelector({ fallbackLabel, savedLocations, activeLocation, onChange }: {
  fallbackLabel: string;
  savedLocations: SavedLocation[];
  activeLocation: { zipcode: string; name?: string } | null;
  onChange: (zipcode: string) => void;
}) {
  const label = activeLocation?.name || fallbackLabel;

  if (!activeLocation || savedLocations.length < 2) {
    return label ? <span className="location-name">{label}</span> : null;
  }

  return (
    <span className="location-switcher">
      <span className="location-switcher-label" aria-hidden="true">
        {label}
        <span className="location-switcher-caret">▾</span>
      </span>
      <select
        className="location-switcher-select"
        value={activeLocation.zipcode}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Hunting location"
      >
        {savedLocations.map((loc) => (
          <option key={loc.zipcode} value={loc.zipcode}>
            {loc.name} ({loc.zipcode})
          </option>
        ))}
      </select>
    </span>
  );
}

export default LocationSwitcher;
